import { onMounted, onBeforeUnmount, ref } from "vue";
import type { Ref } from "vue";
import Popover from "bootstrap/js/dist/popover";

import { unrefElement } from "@vueuse/core";
import {
  ALL_POINTERS,
  installTooltipPointerTracker,
  isPenRecentlyActive,
  isPointerDown,
  onAnyPointerDown,
  onAnyPointerRelease,
  primaryPointerCanHover,
} from "./tooltipPointerTracker";
import { zoomedReferenceModifier } from "@/utils/zoomedPopperReference";

/**
 * Déplacement (px) au-delà duquel un appui n'est plus un long press mais un
 * geste (défilement, glisser) — même seuil que vueuse `onLongPress`.
 */
const LONG_PRESS_MOVE_THRESHOLD_PX = 10;

// Un seul tooltip ouvert à la fois, tous boutons confondus. Sans cette
// coordination, presser un bouton adjacent en laisse un ouvert et déclenche
// un show() qui court contre le hide() du précédent — d'où le clignotement,
// dont la reproductibilité dépend de la livraison des événements pointer par
// l'appareil.
let closeActiveTooltip: (() => void) | null = null;

/**
 * Le bouton peut-il encore recevoir l'appui en cours ? Un bouton masqué,
 * désactivé ou passé en `pointer-events: none` (cf. `.drawing-active`) pendant
 * l'appui ne recevra jamais son relâchement : il ne doit pas non plus ouvrir de
 * tooltip.
 */
function canStillBePressed(element: HTMLElement): boolean {
  if (!element.isConnected || element.getClientRects().length === 0) {
    return false;
  }
  if ((element as HTMLButtonElement).disabled) return false;
  return getComputedStyle(element).pointerEvents !== "none";
}

/**
 * Tooltip d'un bouton de l'outil dessin : appui long sur écran tactile (doigt
 * ou stylet), survol prolongé à la souris sur ordinateur.
 *
 * Règles qui empêchent un tooltip de s'ouvrir sans appui long, ou de rester
 * affiché :
 *  - le relâchement est suivi au niveau de la page (tooltipPointerTracker), pas
 *    seulement sur le bouton : le doigt levé ailleurs, ou sur un bouton devenu
 *    inerte, annule bien le minuteur ;
 *  - à l'échéance, on vérifie que le pointeur est TOUJOURS posé et que le bouton
 *    peut encore être pressé ;
 *  - un appui tactile pendant ou juste après un trait de stylet est ignoré (la
 *    paume posée sur la barre d'outils en écrivant) ;
 *  - tout autre appui dans la page referme le tooltip ouvert ;
 *  - le survol n'est actif que si le pointeur principal de l'appareil sait
 *    survoler (ordinateur), jamais sur tablette.
 */
export function useLongPressTooltip(options?: {
  delayInMs?: number;
  hoverDelayInMs?: number;
  pressExactTarget: boolean;
}): {
  hide: () => void;
  refHook: Ref<HTMLElement | null>;
} {
  const populatedOptions = {
    delayInMs: 800,
    hoverDelayInMs: 500,
    pressExactTarget: false,
    ...options,
  };
  const buttonRefHook = ref<HTMLElement | null>(null);
  let tooltip: Popover;
  let isDisposed = false;

  let isShown = false;
  const hideTooltip = () => {
    if (closeActiveTooltip === hideTooltip) {
      closeActiveTooltip = null;
    }
    if (!isShown || isDisposed || !tooltip) {
      return;
    }
    isShown = false;
    tooltip.hide();
  };
  // Affiche ce tooltip en fermant d'abord celui d'un autre bouton. Idempotent :
  // un second déclencheur pendant qu'il est déjà visible ne relance pas show().
  const showTooltip = () => {
    if (isShown || isDisposed || !tooltip) {
      return;
    }
    if (closeActiveTooltip && closeActiveTooltip !== hideTooltip) {
      closeActiveTooltip();
    }
    closeActiveTooltip = hideTooltip;
    isShown = true;
    tooltip.show();
  };

  // ── Appui long (tactile, stylet, et souris sur un appareil tactile) ───────
  let pressTimer: ReturnType<typeof setTimeout> | undefined;
  let pressStart: { x: number; y: number } | undefined;
  /** Pointeur de l'appui en cours, conservé jusqu'à son relâchement. */
  let pressPointerId: number | undefined;

  const clearPressTimer = () => {
    if (pressTimer !== undefined) {
      clearTimeout(pressTimer);
      pressTimer = undefined;
    }
    pressStart = undefined;
  };
  const endPress = () => {
    clearPressTimer();
    pressPointerId = undefined;
    hideTooltip();
  };

  const onPressStart = (event: PointerEvent) => {
    clearPressTimer();
    pressPointerId = undefined;
    // Le survol prend le relais à la souris sur ordinateur (cf. onPointerEnter).
    if (event.pointerType === "mouse" && primaryPointerCanHover()) return;
    // Bouton secondaire, gomme du stylet…
    if (event.button > 0) return;
    // Paume posée sur la barre d'outils pendant qu'on écrit au stylet.
    if (event.pointerType === "touch" && isPenRecentlyActive()) return;
    if (populatedOptions.pressExactTarget) {
      if (event.target !== unrefElement(buttonRefHook.value)) {
        return;
      }
    }
    const element = unrefElement(buttonRefHook) as HTMLElement | undefined;
    const pointerId = event.pointerId;
    pressPointerId = pointerId;
    pressStart = { x: event.clientX, y: event.clientY };
    pressTimer = setTimeout(() => {
      pressTimer = undefined;
      pressStart = undefined;
      // Démonté pendant l'appui : l'instance Popover est déjà libérée.
      if (isDisposed || !element) return;
      // Dernier rempart : quoi qu'il soit arrivé aux événements, on n'ouvre
      // que si le doigt est réellement encore posé sur un bouton pressable.
      if (pressPointerId !== pointerId || !isPointerDown(pointerId)) return;
      if (!canStillBePressed(element)) return;
      showTooltip();
    }, populatedOptions.delayInMs);
  };

  const onPressMove = (event: PointerEvent) => {
    if (!pressStart || event.pointerId !== pressPointerId) {
      return;
    }
    const dx = event.clientX - pressStart.x;
    const dy = event.clientY - pressStart.y;
    if (Math.hypot(dx, dy) >= LONG_PRESS_MOVE_THRESHOLD_PX) {
      clearPressTimer();
    }
  };

  // ── Survol à la souris (ordinateur uniquement) ──────────────────────────
  let hoverTimer: ReturnType<typeof setTimeout> | undefined;
  const clearHoverTimer = () => {
    if (hoverTimer !== undefined) {
      clearTimeout(hoverTimer);
      hoverTimer = undefined;
    }
  };
  const onPointerEnter = (event: PointerEvent) => {
    if (event.pointerType !== "mouse" || !primaryPointerCanHover()) {
      return;
    }
    clearHoverTimer();
    hoverTimer = setTimeout(() => {
      hoverTimer = undefined;
      if (isDisposed) {
        return;
      }
      showTooltip();
    }, populatedOptions.hoverDelayInMs);
  };
  const onPointerDown = (event: PointerEvent) => {
    clearHoverTimer();
    hideTooltip();
    onPressStart(event);
  };
  // pointerleave rather than pointerout: pointerout also fires when the
  // pointer moves onto a child (the icon), which would make the tooltip
  // flicker while hovering the button.
  const onPointerLeave = () => {
    clearHoverTimer();
    endPress();
  };
  const preventContextMenu = (e: Event) => e.preventDefault();

  // ── Écoute au niveau de la page ──────────────────────────────────────────
  // Relâchement du pointeur de l'appui, où qu'il ait lieu.
  const stopReleaseWatch = onAnyPointerRelease((pointerId) => {
    if (pressPointerId === undefined) return;
    if (pointerId === ALL_POINTERS || pointerId === pressPointerId) endPress();
  });
  // Tout autre appui dans la page : le tooltip ouvert se referme, et un appui
  // long en attente est abandonné (deuxième doigt, stylet qui se pose…).
  const stopDownWatch = onAnyPointerDown((event) => {
    if (event.pointerId === pressPointerId) return;
    const element = unrefElement(buttonRefHook) as HTMLElement | undefined;
    if (
      element &&
      event.target instanceof Node &&
      element.contains(event.target)
    ) {
      return; // Appui sur ce bouton : géré par onPointerDown.
    }
    clearHoverTimer();
    clearPressTimer();
    pressPointerId = undefined;
    hideTooltip();
  });

  let observer: MutationObserver | undefined;

  onMounted(() => {
    installTooltipPointerTracker();
    // ref.value can be either the HtmlElement or a Component depending if it was `ref=""` on a node or a Component
    const htmlElement = unrefElement(buttonRefHook);
    if (htmlElement) {
      tooltip = new Popover(htmlElement, {
        trigger: "manual",
        html: true,
        animation: false,
        customClass: "dr-button-popover",
        // Bouton zoomé (taille des icônes) : sur Safari et les anciens
        // Chromium, l'infobulle se plaçait en face d'un autre outil.
        popperConfig: (defaults) => ({
          ...defaults,
          modifiers: [...(defaults.modifiers ?? []), zoomedReferenceModifier],
        }),
      });
      htmlElement.addEventListener("pointerdown", onPointerDown);
      htmlElement.addEventListener("pointermove", onPressMove);
      htmlElement.addEventListener("pointerup", endPress);
      htmlElement.addEventListener("pointercancel", endPress);
      htmlElement.addEventListener("lostpointercapture", endPress);
      htmlElement.addEventListener("pointerleave", onPointerLeave);
      htmlElement.addEventListener("pointerenter", onPointerEnter);
      htmlElement.addEventListener("contextmenu", preventContextMenu);

      observer = new MutationObserver(() => {
        tooltip?.setContent({
          ".popover-header": htmlElement.getAttribute("data-bs-title") ?? "",
          ".popover-body": htmlElement.getAttribute("data-bs-content") ?? "",
        });
      });
      observer.observe(htmlElement, {
        attributes: true,
        attributeFilter: ["data-bs-content", "data-bs-title"],
      });
    }
  });

  onBeforeUnmount(() => {
    isDisposed = true;
    if (closeActiveTooltip === hideTooltip) {
      closeActiveTooltip = null;
    }
    clearHoverTimer();
    clearPressTimer();
    stopReleaseWatch();
    stopDownWatch();
    const htmlElement = unrefElement(buttonRefHook);
    observer?.disconnect();
    tooltip?.dispose();

    htmlElement?.removeEventListener("pointerdown", onPointerDown);
    htmlElement?.removeEventListener("pointermove", onPressMove);
    htmlElement?.removeEventListener("pointerup", endPress);
    htmlElement?.removeEventListener("pointercancel", endPress);
    htmlElement?.removeEventListener("lostpointercapture", endPress);
    htmlElement?.removeEventListener("pointerleave", onPointerLeave);
    htmlElement?.removeEventListener("pointerenter", onPointerEnter);
    htmlElement?.removeEventListener("contextmenu", preventContextMenu);
  });

  return { hide: hideTooltip, refHook: buttonRefHook };
}
